// ═══════════════════════════════════════════════════════════════════════════
// RUN 11: Table Text Styling
// ═══════════════════════════════════════════════════════════════════════════

import { reportResult, paraDetail, RunDetail, applydefaultparagraphformat } from '../runs/common'; // FIX S1128: removed unused 'safeAlert'
import { ensureRunStyles } from '../taskpane/styleEngine';
import { getConfig } from '../taskpane/configService';
import { isListLikeParagraph } from '../runs/listDetection';

/** Check if a style is special (should be skipped) — driven by config */
function isSpecialStyle(s: string, skipPatterns: string[]): boolean {
  if (!s) return false;
  const l = s.toLowerCase().trim();
  return skipPatterns.some(pattern => l.includes(pattern));
}

// ── Shared counter shape ─────────────────────────────────────────────────
interface RunCounters {
  totalChecked: number;
  skippedCount: number;
  listSkipped: number;
  converted: number;
}

// ── Config type alias ────────────────────────────────────────────────────
type Run11Config = ReturnType<typeof getConfig>["run11_tableText"];

async function tableHasCaption(
  table: Word.Table,
  context: Word.RequestContext,
  captionRegex: RegExp,
  captionMustNotEndWith: string,
  captionStopPatterns: string[]
): Promise<boolean> {
  // ── Check caption inside first row ──
  table.rows.load("items");
  await context.sync();

  if (table.rows.items.length === 0) return false;

  const firstRow = table.rows.items[0];
  firstRow.cells.load("items");
  await context.sync();

  if (firstRow.cells.items.length === 0) return false;

  const firstCell = firstRow.cells.items[0];
  firstCell.load("body");
  await context.sync();

  const paras = firstCell.body.paragraphs;
  paras.load("items");
  await context.sync();

  let firstRowText = "";
  for (const para of paras.items) {
    para.load("text");
    await context.sync();
    firstRowText += (para.text || "").trim() + " ";
  }

  // Config-driven regex: must match caption pattern AND must not end with captionMustNotEndWith
  if (
    captionRegex.test(firstRowText) &&
    !firstRowText.toLowerCase().trimEnd().endsWith(captionMustNotEndWith)
  ) {
    console.log(`  → Caption INSIDE first row detected`);
    return true;
  }

  // ── Search backward for external caption (same logic as run3) ──
  let currentPara = table.getParagraphBeforeOrNullObject();
  currentPara.load("isNullObject");
  await context.sync();

  while (!currentPara.isNullObject) {
    currentPara.load("isNullObject, text, styleBuiltIn, style");
    await context.sync();
    if (currentPara.isNullObject) break;

    const parentTable = currentPara.parentTableOrNullObject;
    parentTable.load("isNullObject");
    await context.sync();

    if (!parentTable.isNullObject) {
      console.log(`  External caption search: walked into a table — stopping.`);
      break;
    }

    const paraText = (currentPara.text || "").trim();
    const paraStyle = currentPara.style || "";

    // Config-driven stop patterns (e.g. heading, toc, hyperlink, table of)
    const styleLower = paraStyle.toLowerCase();
    const builtInLower = (currentPara.styleBuiltIn || "").toLowerCase();
    const shouldStop = captionStopPatterns.some(p => styleLower.includes(p) || builtInLower.includes(p));
    if (shouldStop) break;

    if (
      captionRegex.test(paraText) &&
      !paraText.toLowerCase().endsWith(captionMustNotEndWith)
    ) {
      console.log(`  → Caption OUTSIDE detected: "${paraText.substring(0, 50)}"`);
      return true;
    }

    currentPara = currentPara.getPreviousOrNullObject();
    currentPara.load("isNullObject");
    await context.sync();
  }

  return false;
}

// ── FIX S3776: extracted helper — process a single cell paragraph ────────
async function processCellParagraph(
  para: Word.Paragraph,
  context: Word.RequestContext,
  tableTextStyle: string,
  skipStylePatterns: string[],
  counters: RunCounters,
  changedParas: RunDetail[],
  label: string
): Promise<void> {
  para.load("text, style, uniqueLocalId, styleBuiltIn");
  await context.sync();

  counters.totalChecked++;

  const pText = (para.text || "").trim();
  const pStyle = (para.style || "").trim();
  let ooxmlStyle = "";

  if (!pText) return;

  if (!pStyle) {
    try {
      const ooxmlResult = para.getOoxml();
      await context.sync();
      const match = ooxmlResult.value.match(/<w:pStyle\s+w:val="([^"]+)"/);
      if (match) {
        ooxmlStyle = match[1];
        console.log(`  [OOXML fallback] Found style: "${ooxmlStyle}"`);
      }
    } catch (e) {
      console.log(`  [OOXML fallback] Failed: ${e.message}`);
    }
  }

  console.log(`  [${label}] Style raw: "${para.style}" | trimmed: "${pStyle}"`);

  // Config-driven skip patterns
  if (isSpecialStyle(pStyle, skipStylePatterns)) {
    console.log(`  → Skipped (special style): "${pStyle}"`);
    counters.skippedCount++;
    return;
  }
  if (pStyle === tableTextStyle) {
    counters.skippedCount++;
    return;
  }

  const listCheck = await isListLikeParagraph(para, context, pText, pStyle);
  if (listCheck.isList) {
    console.log(`  → LIST-SKIP (${listCheck.reason}): "${pText.substring(0, 50)}"`);
    counters.listSkipped++;
    return;
  }

  console.log(`[${label}] "${pStyle} built ${para.styleBuiltIn} ooxmlStyle ${ooxmlStyle}" → "${tableTextStyle}" | "${pText.substring(0, 40)}"`);

  para.style = tableTextStyle;
  await context.sync();
  await applydefaultparagraphformat(para, tableTextStyle, context);

  changedParas.push(paraDetail(para.uniqueLocalId, pText, `→ ${tableTextStyle}`));
  counters.converted++;
}

// ── FIX S3776: extracted helper — header row majority vote ───────────────
async function isHeaderRow(
  row: Word.TableRow,
  context: Word.RequestContext,
  headerStylePrefix: string,
  headerDetectBold: boolean
): Promise<boolean> {
  let headerLike = 0;
  let normal = 0;

  for (const cell of row.cells.items) {
    const checkParas = cell.body.paragraphs;
    checkParas.load("items");
    await context.sync();

    for (const cp of checkParas.items) {
      cp.load("style, styleBuiltIn, font, text");
      await context.sync();

      // FIX S7735: replaced negated condition with positive guard block
      if ((cp.text || "").trim()) {
        const isHeaderApi =
          (cp.style || "").startsWith(headerStylePrefix) ||
          (cp.styleBuiltIn || "").startsWith(headerStylePrefix) ||
          (headerDetectBold && cp.font.bold === true);

        if (isHeaderApi) headerLike++; else normal++;
      }
    }
  }

  return headerLike > normal;
}

// ── Helper: collect text from the first cell of a row ───────────────────
async function getFirstCellText(
  row: Word.TableRow,
  context: Word.RequestContext
): Promise<string> {
  const firstCellParas = row.cells.items[0].body.paragraphs;
  firstCellParas.load("items");
  await context.sync();

  let rowText = "";
  for (const p of firstCellParas.items) {
    p.load("text");
    await context.sync();
    rowText += (p.text || "").trim() + " ";
  }
  return rowText;
}

// ── Helper: check whether row 0 is an inline caption row ────────────────
async function isInlineCaptionRow(
  row: Word.TableRow,
  context: Word.RequestContext,
  captionRegex: RegExp,
  captionMustNotEndWith: string
): Promise<boolean> {
  const rowText = await getFirstCellText(row, context);
  const trimmed = rowText.trim();
  return captionRegex.test(trimmed) && !trimmed.toLowerCase().endsWith(captionMustNotEndWith);
}

// ── Helper: process all cells in a single body row ───────────────────────
async function processRowCells(
  row: Word.TableRow,
  context: Word.RequestContext,
  cfg: Run11Config,
  counters: RunCounters,
  changedParas: RunDetail[],
  rowLabel: string
): Promise<void> {
  for (let c = 0; c < row.cells.items.length; c++) {
    const cellParas = row.cells.items[c].body.paragraphs;
    cellParas.load("items");
    await context.sync();

    for (let p = 0; p < cellParas.items.length; p++) {
      await processCellParagraph(
        cellParas.items[p],
        context,
        cfg.tableTextStyle,
        cfg.skipStylePatterns,
        counters,
        changedParas,
        `${rowLabel} C${c + 1} P${p + 1}`
      );
    }
  }
}

// ── FIX S3776 + S7735: processTable delegates to focused helpers ─────────
async function processTable(
  table: Word.Table,
  tableIndex: number,
  context: Word.RequestContext,
  cfg: Run11Config,
  captionRegex: RegExp,
  counters: RunCounters,
  changedParas: RunDetail[]
): Promise<void> {
  table.rows.load("items");
  await context.sync();

  const totalRows = table.rows.items.length;

  // Check if last row is a footnote row (only 1 cell) — config-driven
  const lastRow = table.rows.items[totalRows - 1];
  lastRow.cells.load("items");
  await context.sync();
  const excludeLastRow =
    cfg.excludeLastRowIfSingleCell &&
    totalRows > 1 &&
    lastRow.cells.items.length === 1;

  // FIX S7735: use 0 as default; set to 1 if row 0 is a caption row.
  // Eliminates the captionRowIndex !== -1 negated ternary entirely.
  let headerRowIndex = 0;

  for (let r = 0; r < totalRows; r++) {
    const row = table.rows.items[r];
    const tag = `T${tableIndex + 1} R${r + 1}`;

    if (excludeLastRow && r === totalRows - 1) {
      console.log(`[${tag}] Footnote row (1 cell) — skipping`);
      continue;
    }

    row.cells.load("items");
    await context.sync();

    // Detect inline caption in row 0 and skip it
    if (r === 0) {
      const isCaptionRow = await isInlineCaptionRow(row, context, captionRegex, cfg.captionMustNotEndWith);
      if (isCaptionRow) {
        console.log(`[${tag}] Caption row — skipping`);
        headerRowIndex = 1; // header follows the caption
        continue;
      }
    }

    // Header row: majority vote on the resolved header row index
    if (r === headerRowIndex) {
      const isHeader = await isHeaderRow(row, context, cfg.headerStylePrefix, cfg.headerDetectBold);
      if (isHeader) {
        console.log(`[${tag}] Header row — skipping`);
        continue;
      }
    }

    await processRowCells(row, context, cfg, counters, changedParas, tag);
  }
}

export async function run11() {
  await Word.run(async (context) => {
    try {
      await ensureRunStyles(context, 11);
      context.document.changeTrackingMode = Word.ChangeTrackingMode.trackAll;

      // ── Load config ──────────────────────────────────────────────────────
      const cfg = getConfig().run11_tableText;
      // FIX S1854: removed unused charResetStyle assignment

      const captionRegex = new RegExp(cfg.captionRegex, "i");

      // ── Counters ─────────────────────────────────────────────────────────
      const counters: RunCounters = {
        totalChecked: 0,
        skippedCount: 0,
        listSkipped: 0,
        converted: 0,
      };
      const changedParas: RunDetail[] = [];
      let skippedTables = 0;

      console.log("=".repeat(70));
      console.log("=== TABLE TEXT STYLING ===");
      console.log("=".repeat(70));

      const tables = context.document.body.tables;
      tables.load("items");
      await context.sync();

      console.log(`Tables found: ${tables.items.length}\n`);

      for (let t = 0; t < tables.items.length; t++) {
        const table = tables.items[t];

        const hasCaption = await tableHasCaption(
          table, context,
          captionRegex, cfg.captionMustNotEndWith, cfg.captionStopPatterns
        );

        if (!hasCaption) {
          console.log(`[Table ${t + 1}] No caption found — skipping entire table\n`);
          skippedTables++;
          continue;
        }

        await processTable(table, t, context, cfg, captionRegex, counters, changedParas);
      }

      context.document.changeTrackingMode = Word.ChangeTrackingMode.off;
      await context.sync();

      console.log("\n" + "=".repeat(70));
      console.log(`Tables skipped (no caption): ${skippedTables}`);
      console.log(`Checked: ${counters.totalChecked} | Skipped: ${counters.skippedCount} | List-skipped: ${counters.listSkipped} (handled by Run 25) | Converted: ${counters.converted}`);
      console.log("✓ Table Text styling complete!");

      if (counters.converted > 0) {
        reportResult('Table Text Styling Complete', [
          { label: `Table cells converted to ${cfg.tableTextStyle} style`, count: counters.converted },
          { label: 'Paragraphs checked', count: counters.totalChecked },
          { label: 'List/bullet paragraphs skipped', count: counters.listSkipped, note: 'handled by Run 25' },
          ...changedParas
        ]);
      } else {
        reportResult('Table Text Styling — no changes needed', [
          { label: 'All table text already correct', note: 'No changes made' },
          { label: 'List/bullet paragraphs skipped', count: counters.listSkipped, note: 'handled by Run 25' },
        ]);
      }
    } catch (error) {
      console.error("Error in run11:", error);
      console.error("Message:", error.message);
    }
  });
}